import { arrayMove } from "@dnd-kit/sortable";
import { useConfirmingLeave } from "@lhd/app/index";
import { HIDDENS_COLUMNS_SHEET } from "@lhd/features/Apps/constants";
import { createFieldCode, generateUuid } from "@lhd/features/Apps/utils";
import { cloneDeep } from "lodash";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { FormActionContext } from "../..";
import {
  APP_PART_TYPES,
  KEY_MENU,
  DEFAULT_WIDTH_COL,
  TAGS,
} from "../../constants";
import { useGetListDataExist } from "../../hook";
import { AppPartType } from "../../types";

export type ReceivedProps = Record<string, any>;

const useAppTable = (props: ReceivedProps) => {
  const { layout } = props;
  const [editingAppPart, setEditingAppPart] = useState<AppPartType>();
  const { checkLeaveForm } = useConfirmingLeave();
  const { dataExistList } = useGetListDataExist();
  const containerRef = useRef<HTMLDivElement>(null);

  const optionsType = useMemo(() => {
    return TAGS.map((tagItem) => ({
      label: tagItem?.name,
      value: tagItem?.app_part_type,
    })).filter((x) => !HIDDENS_COLUMNS_SHEET.includes(x.value));
  }, []);

  const { onHandleDeleteItem, onHandleDuplicate, setContextState } =
    useContext(FormActionContext);

  const handleChangeAppById = useCallback(
    (appId: string, type: any, value: any, isEdit?: boolean) => {
      setContextState((draft) => {
        const isError =
          type === "field_code" || type === "name" ? !value : draft.isError;
        draft.isError = isError;

        const parseConfig = (config: any) => {
          if (!config) return {};
          if (typeof config === "string") {
            try {
              return JSON.parse(config);
            } catch {
              return {};
            }
          }
          return config;
        };

        const targetItem = draft.layout.find((item: any) =>
          appId?.includes(item.uuid ?? String(item.id)),
        );

        if (targetItem) {
          const oldType = targetItem.app_part_type;
          const isChangeToRichtext =
            type === "app_part_type" && value === APP_PART_TYPES.RICH_TEXT;

          if (isChangeToRichtext) {
            const tag = TAGS.find((tag) => tag.app_part_type === value);
            if (tag) {
              targetItem.item_config_data = {
                ...targetItem.item_config_data,
                ...tag.item_config_data,
              };
            }
          }

          if ((targetItem as any)[type]) {
            (targetItem as any)[type] = value;
          }

          if (type === "app_part_type" && oldType !== value) {
            const itemConfig = parseConfig(targetItem.item_config_data);
            const parentI = itemConfig?.employee_info_parent_i;
            if (parentI) {
              const updateParent = (parent: AppPartType) => {
                const pConfig = parseConfig(parent.item_config_data);
                if (
                  pConfig?.employee_info_children &&
                  Array.isArray(pConfig.employee_info_children)
                ) {
                  pConfig.employee_info_children =
                    pConfig.employee_info_children.map((t: string) =>
                      t === oldType ? value : t,
                    );
                  if (!pConfig.employee_info_children.includes(value)) {
                    pConfig.employee_info_children.push(value);
                  }
                  parent.item_config_data = pConfig;
                }
              };

              const parentInLayout = draft.layout.find((p: any) => {
                const pConfig = parseConfig(p.item_config_data);
                return pConfig?.i === parentI;
              });
              if (parentInLayout) {
                updateParent(parentInLayout);
              } else {
                for (const layoutItem of draft.layout) {
                  if (layoutItem.children) {
                    const childParent = layoutItem.children.find((c: any) => {
                      const pConfig = parseConfig(c.item_config_data);
                      return pConfig?.i === parentI;
                    });
                    if (childParent) {
                      updateParent(childParent);
                      break;
                    }
                  }
                }
              }
            }
          }
        }
      });
    },
    [setContextState],
  );

  const onAddNewColumn = useCallback(
    ({
      position = "prev",
      appOriginal,
    }: {
      position: "prev" | "next";
      appOriginal: AppPartType;
    }) => {
      checkLeaveForm();

      setContextState((draft) => {
        let maxHeightItemApp = 0;

        const ids = draft?.layout.map((l: AppPartType) => {
          if (l.item_config_data.y + l.item_config_data.h > maxHeightItemApp) {
            maxHeightItemApp = l.item_config_data.y + l.item_config_data.h;
          }
          const id = l.item_config_data?.i?.split("-");
          return Number(id[id.length - 1]);
        });

        let newItem: AppPartType = {} as AppPartType;
        const firstItem = draft?.layout?.[0];
        if (firstItem) {
          const clonedAppPart = cloneDeep(firstItem);
          const tag = TAGS.find(
            (tag) => tag.app_part_type === APP_PART_TYPES.TEXT,
          );

          newItem = {
            app_part_type: APP_PART_TYPES.TEXT,
            id: undefined,
            uuid: generateUuid(),
            name: tag?.name,
            item_config_data: {
              ...clonedAppPart.item_config_data,
              ...tag?.item_config_data,
              parentI: undefined,
              i: generateUuid(),
              x: 0,
              y: maxHeightItemApp,
            },
            width: DEFAULT_WIDTH_COL,
            current_detail_permissions: {
              specified_app_part_edit: true,
              specified_app_part_read: true,
              add_new_app_part: true,
            },
          } as AppPartType;
        } else {
          const tag = TAGS.find(
            (tag) => tag.app_part_type === APP_PART_TYPES.TEXT,
          );
          newItem = {
            app_part_type: APP_PART_TYPES.TEXT,
            id: undefined,
            uuid: generateUuid(),
            name: tag?.name,
            item_config_data: {
              i: generateUuid(),
              x: 0,
              y: 1,
              ...tag?.item_config_data,
            },
            width: DEFAULT_WIDTH_COL,
            current_detail_permissions: {
              specified_app_part_edit: true,
              specified_app_part_read: true,
              add_new_app_part: true,
            },
          } as AppPartType;
        }
        newItem.field_code = createFieldCode(newItem.name, layout);
        const indexAppOriginal = draft.layout.findIndex((appItem) =>
          appOriginal.id
            ? appItem.id === appOriginal.id
            : appItem.uuid === appOriginal.uuid,
        );

        const newLayout = [...draft.layout];
        newLayout.splice(
          position === "prev" ? indexAppOriginal : indexAppOriginal + 1,
          0,
          newItem,
        );

        draft.layout = newLayout;
      });
    },
    [checkLeaveForm, setContextState, layout],
  );

  const handleMenuClick = useCallback(
    (appPart, type) => {
      switch (type) {
        case KEY_MENU.EDIT:
          setContextState((draft) => {
            draft.isDisableDragging = true;
          });
          setEditingAppPart({ ...appPart });
          break;
        case KEY_MENU.DUPLICATE:
          onHandleDuplicate({
            originalItem: { ...appPart, uuid: generateUuid() },
            parentI:
              appPart.app_part_type === "field_group"
                ? appPart.item_config_data.parentI
                : "",
          });
          break;
        case KEY_MENU.DELETE:
          onHandleDeleteItem({
            i: appPart.item_config_data.i,
            parentI: appPart.item_config_data.parentI,
          });
          break;
        case KEY_MENU.ADD_TO_LEFT:
          onAddNewColumn({
            position: "prev",
            appOriginal: appPart,
          });
          break;
        case KEY_MENU.ADD_TO_RIGHT:
          onAddNewColumn({
            position: "next",
            appOriginal: appPart,
          });
          break;
      }
    },
    [setContextState, onHandleDuplicate, onHandleDeleteItem, onAddNewColumn],
  );

  const handleUpdateWidth = (newWidth: number, appPart?: AppPartType) => {
    setContextState((draft) => {
      const targetItem = draft.layout?.find(
        (item) =>
          (item.id && item.id === appPart?.id) ||
          (item.uuid && item.uuid === appPart?.uuid),
      );
      if (targetItem) {
        targetItem.width = newWidth;
      }
    });
  };

  const onDragging = (indexDragging: number, x: number) => {
    if (!containerRef.current) return;
    const itemContainers =
      containerRef.current.querySelectorAll(".item_container");
    if (!itemContainers) return;
    let preWidth = 0;
    let leftLimit = 0;
    let rightLimit = 0;

    let overIndex = indexDragging;

    itemContainers.forEach((item, index) => {
      if (index < indexDragging) {
        leftLimit += (item as HTMLDivElement).offsetWidth;
      } else if (index > indexDragging) {
        rightLimit += (item as HTMLDivElement).offsetWidth;
      }
    });

    const draggingWidth = (itemContainers[indexDragging] as HTMLElement)
      .offsetWidth;
    if (x < 0) {
      for (let index = indexDragging; index >= 0; index--) {
        const itemAs = itemContainers[index] as HTMLDivElement;
        if (indexDragging === index) {
          if (x + leftLimit >= 0) {
            itemAs.style.transform = `translate(${x}px, 0px)`;
          } else {
            itemAs.style.transform = `translate(-${leftLimit}px, 0px)`;
          }
        } else {
          const currentWidth = itemAs.offsetWidth;
          preWidth += currentWidth;
          const remainWidth = preWidth + x;
          if (currentWidth / 2 > remainWidth) {
            itemAs.style.transform = `translate(${draggingWidth}px, 0px)`;
            overIndex = index;
          } else {
            itemAs.style.transform = `translate(0px, 0px)`;
          }
        }
      }
    } else {
      for (let index = indexDragging; index < itemContainers.length; index++) {
        const itemAs = itemContainers[index] as HTMLDivElement;
        if (indexDragging === index) {
          if (x < rightLimit) {
            itemAs.style.transform = `translate(${x}px, 0px)`;
          } else {
            itemAs.style.transform = `translate(${rightLimit}px, 0px)`;
          }
        } else {
          const currentWidth = itemAs.offsetWidth;
          preWidth += currentWidth;
          const remainWidth = preWidth - x;
          if (currentWidth / 2 > remainWidth) {
            itemAs.style.transform = `translate(-${draggingWidth}px, 0px)`;
            overIndex = index;
          } else {
            itemAs.style.transform = `translate(0px, 0px)`;
          }
        }
      }
    }

    return overIndex;
  };

  const onDragStopped = (activeIndex: number, overIndex: number) => {
    setContextState((draft) => {
      draft.layout = arrayMove(draft.layout, activeIndex, overIndex);
      if (!containerRef.current) return;
      const itemContainers =
        containerRef.current.querySelectorAll(".item_container");
      if (!itemContainers) return;
      itemContainers.forEach(
        (item) =>
          ((item as HTMLDivElement).style.transform = `translate(0px, 0px)`),
      );
    });
  };

  return {
    ...props,
    optionsType,
    handleChangeAppById,
    handleMenuClick,
    dataExistList,
    editingAppPart,
    setEditingAppPart,
    handleUpdateWidth,
    onDragging,
    onDragStopped,
    containerRef,
  };
};

export type Props = ReturnType<typeof useAppTable>;

export default useAppTable;
